/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ServerErrorHero} from 'common/widgets/warning-hero/presets';
import {WarningHeroReportName} from 'common/widgets/warning-hero/constants';
import {UsersTableEl} from 'common/widgets/users-table';
import {SpinnerEl} from 'common/components/spinner';
import {InputEl} from 'common/components/input';
import {ButtonEl} from 'common/components/button';
import {ButtonLayout, ButtonTheme} from 'common/components/button/constants';
import {CreateCohortButtonEl} from 'common/widgets/create-cohort-button';
import {pluralize} from 'common/util/string';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Permission} from 'common/report/constants';
import {TooltipEl} from 'common/components/tooltip';

import {TrackingEvent} from '../tracking';
import type {SidebarUserList} from './index';

export function template(this: SidebarUserList) {
  const segments = this.attr(`segments`);
  const {loadingCount, totalUsers = 0, loading, users, error, cohort} = this.state;
  const dgContext = this.attr(`data-group-name`) === `user` ? `user` : undefined;
  const {t, print} = this.getContext(`i18n`);
  return (
    <div sel='.wrapper'>
      <div sel='.header'>
        <h2>
          {t(`view-users.title`, {
            context: dgContext,
            group: pluralize(this.attr(`data-group-name`), true),
          })}
          {this.attr(`data-group-name`)?.length > 25 && (
            <TooltipEl
              attrs={{
                text: `View ${pluralize(this.attr(`data-group-name`), true)}`,
              }}
            />
          )}
        </h2>
        <slot />
      </div>
      <div sel='.info'>
        <h3>
          <span sel='.color' style={{background: this.attr(`color`)}} />
          {segments.join(` / `)}
        </h3>
        <p>
          {!loadingCount ? totalUsers.toLocaleString() : <SpinnerEl />}
          {` `}
          {this.params.createSegmentLabel
            ? print(
                this.params.createSegmentLabel({
                  context: this.attr(`data-group-name`),
                  count: totalUsers,
                  group: pluralize(this.attr(`data-group-name`), totalUsers),
                }),
              )
            : t(`view-users.count-label`, {
                context: dgContext,
                count: totalUsers,
                group: pluralize(this.attr(`data-group-name`), totalUsers),
              })}
        </p>
        <div sel='.actions'>
          <ButtonEl
            attrs={{
              theme: ButtonTheme.Tertiary,
              icon: `export-csv`,
              layout: ButtonLayout.Vertical,
            }}
            on={{
              click: this.handleDownloadCsv,
            }}
          >
            {t(`view-users.export-csv`)}
          </ButtonEl>
          {currentProjectStore.value?.hasPermission(Permission.WriteCohorts) &&
            !this.attr(`hide-cohort-functionality`) && (
              <CreateCohortButtonEl
                attrs={{
                  label: t(`view-users.create-cohort`),
                  icon: `community`,
                  theme: ButtonTheme.Tertiary,
                  layout: ButtonLayout.Vertical,
                  'show-upsell-in-dropdown': true,
                }}
                props={{
                  cohortDetails: this.state.cohortDetails,
                }}
                on={{
                  create: this.handleCohortEvent(TrackingEvent.CreateCohort),
                  save: this.handleCohortEvent(TrackingEvent.SaveCohort),
                }}
              />
            )}
        </div>
      </div>
      <div sel='.search'>
        <InputEl
          attrs={{
            icon: `search`,
            placeholder: t(`view-users.search-placeholder`),
            'show-clear': true,
            'auto-focus': true,
          }}
          style={{width: `100%`}}
          on={{input: this.handleSearch, clear: this.handleSearch}}
        />
      </div>
      <div sel='.content'>
        {error && <ServerErrorHero reportName={WarningHeroReportName.Users} />}
        {(loading || !error) && (
          <UsersTableEl
            attrs={{
              'data-group-id': cohort.dataGroupId || ``,
              loading,
              'is-select-enabled': false,
              'showing-properties': this.getShowingProperties(),
              'sensitive-properties': [],
              'default-sort-field': {value: `$last_seen`},
              'default-sort-order': -1,
              'emit-row-click': true,
              compact: true,
            }}
            props={{
              rows: users.map((user) => ({
                $distinct_id: user.$distinct_id,
                ...user.$properties,
              })),
            }}
            on={{
              userSelected: this.handleUserSelected,
            }}
          />
        )}
      </div>
    </div>
  );
}
